import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  RefreshCw, 
  Search, 
  Filter,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Pause,
  Play,
  XCircle,
  ChevronDown,
  ChevronRight,
  Settings,
  Users,
  TrendingUp,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { formatCurrency } from '../../utils/formatUtils';

export const AdminSIPManagementPage = () => {
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('mandates'); // mandates, alerts, scheduler
  
  // Mandates state
  const [mandates, setMandates] = useState([]);
  const [mandatesLoading, setMandatesLoading] = useState(true);
  const [mandatesPage, setMandatesPage] = useState(1);
  const [mandatesTotal, setMandatesTotal] = useState(0);
  const [mandatesHasMore, setMandatesHasMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [frequencyFilter, setFrequencyFilter] = useState('');
  const [needsReviewFilter, setNeedsReviewFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [openAlerts, setOpenAlerts] = useState(0);
  
  // Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [triggeringJob, setTriggeringJob] = useState(null);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Dialog states
  const [selectedMandate, setSelectedMandate] = useState(null);
  const [showMandateDetail, setShowMandateDetail] = useState(false);
  const [showResolveAlert, setShowResolveAlert] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolveForm, setResolveForm] = useState({ resolution: '', action_taken: '' });
  const [showExecuteConfirm, setShowExecuteConfirm] = useState(false);
  const [executeReason, setExecuteReason] = useState('');
  const [executing, setExecuting] = useState(false);

  // Fetch mandates
  const fetchMandates = useCallback(async (page = 1) => {
    setMandatesLoading(true);
    try {
      const response = await adminAPI.getSIPMandates(
        page, 
        20, 
        statusFilter, 
        frequencyFilter, 
        needsReviewFilter,
        searchQuery
      );
      setMandates(response.data.mandates);
      setMandatesTotal(response.data.total);
      setMandatesHasMore(response.data.has_more);
      setMandatesPage(page);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Failed to fetch mandates');
      }
    } finally {
      setMandatesLoading(false);
    }
  }, [statusFilter, frequencyFilter, needsReviewFilter, searchQuery]);

  // Fetch alerts
  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      const response = await adminAPI.getSIPAlerts(1, 50, 'open');
      setAlerts(response.data.alerts);
      setOpenAlerts(response.data.open_count);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  // Fetch scheduler status
  const fetchSchedulerStatus = async () => {
    setSchedulerLoading(true);
    try {
      const response = await adminAPI.getSIPSchedulerStatus();
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
    } finally {
      setSchedulerLoading(false);
    }
  };

  // Fetch analytics
  const fetchAnalytics = async () => {
    try {
      const response = await adminAPI.getSIPAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  // Initial load
  useEffect(() => {
    fetchMandates();
    fetchAlerts();
    fetchAnalytics();
  }, [fetchMandates]);

  // Load scheduler when tab changes
  useEffect(() => {
    if (activeTab === 'scheduler') {
      fetchSchedulerStatus();
    }
  }, [activeTab]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchMandates(1);
  };

  // Handle mandate detail view
  const handleViewMandate = async (mandate) => {
    try {
      const response = await adminAPI.getSIPMandateDetails(mandate.mandate_id);
      setSelectedMandate(response.data.mandate);
      setShowMandateDetail(true);
    } catch (error) {
      toast.error('Failed to fetch mandate details');
    }
  };

  // Handle manual execution
  const handleExecuteMandate = async () => {
    if (!executeReason.trim()) {
      toast.error('Please provide a reason for manual execution');
      return;
    }
    
    setExecuting(true);
    try {
      const response = await adminAPI.triggerSIPExecution(selectedMandate.mandate_id, executeReason);
      toast.success('Execution triggered successfully');
      setShowExecuteConfirm(false);
      setExecuteReason('');
      fetchMandates(mandatesPage);
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger execution');
    } finally {
      setExecuting(false);
    }
  };

  // Handle resolve alert
  const handleResolveAlert = async () => {
    if (!resolveForm.resolution.trim() || !resolveForm.action_taken) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      await adminAPI.resolveSIPAlert(selectedAlert.alert_id, resolveForm.resolution, resolveForm.action_taken);
      toast.success('Alert resolved');
      setShowResolveAlert(false);
      setResolveForm({ resolution: '', action_taken: '' });
      fetchAlerts();
      fetchMandates(mandatesPage);
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  // Handle trigger scheduler job
  const handleTriggerJob = async (jobId) => {
    setTriggeringJob(jobId);
    try {
      const response = await adminAPI.triggerSIPSchedulerJob(jobId);
      toast.success(`Job executed: ${JSON.stringify(response.data.result)}`);
      fetchSchedulerStatus();
      fetchAnalytics();
      fetchMandates(mandatesPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger job');
    } finally {
      setTriggeringJob(null);
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-blue-100 text-blue-800',
      expired: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-cream pb-8">
      {/* Header */}
      <div className="navy-gradient text-white px-4 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/10 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-serif text-xl font-semibold">SIP Management</h1>
          </div>
          
          {/* Analytics Summary */}
          {analytics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span className="text-2xl font-bold">{analytics.active_mandates}</span>
                </div>
                <p className="text-xs text-white/70">Active SIPs</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-2xl font-bold">{analytics.executions_today}</span>
                </div>
                <p className="text-xs text-white/70">Todays Debits</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-300" />
                  <span className="text-2xl font-bold">{analytics.alerts_open}</span>
                </div>
                <p className="text-xs text-white/70">Open Alerts</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-2xl font-bold">{formatCurrency(analytics.total_invested)}</span>
                </div>
                <p className="text-xs text-white/70">Total Invested</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('mandates')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'mandates' 
                ? 'border-navy text-navy' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Mandates ({mandatesTotal})
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'alerts' 
                ? 'border-navy text-navy' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Alerts {openAlerts > 0 && <Badge variant="destructive" className="ml-1">{openAlerts}</Badge>}
          </button>
          <button
            onClick={() => setActiveTab('scheduler')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'scheduler' 
                ? 'border-navy text-navy' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Scheduler
          </button>
        </div>

        {/* Mandates Tab */}
        {activeTab === 'mandates' && (
          <div>
            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      placeholder="Search by phone or mandate ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="revoked">Revoked</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Freq</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select 
                    value={needsReviewFilter === null ? '' : needsReviewFilter.toString()} 
                    onValueChange={(v) => setNeedsReviewFilter(v === '' ? null : v === 'true')}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Review" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All</SelectItem>
                      <SelectItem value="true">Needs Review</SelectItem>
                      <SelectItem value="false">No Issues</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="submit" variant="default">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => fetchMandates(mandatesPage)}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Mandates List */}
            <Card>
              <CardContent className="p-0">
                {mandatesLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : mandates.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No mandates found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Exec</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {mandates.map((mandate) => (
                          <tr 
                            key={mandate.mandate_id} 
                            className={`hover:bg-gray-50 ${mandate.needs_review ? 'bg-red-50' : ''}`}
                          >
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{mandate.user_name}</div>
                              <div className="text-xs text-gray-500">{mandate.user_phone}</div>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium">{formatCurrency(mandate.amount)}</td>
                            <td className="px-4 py-3 text-sm capitalize">{mandate.frequency}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(mandate.status)}`}>
                                {mandate.status}
                              </span>
                              {mandate.needs_review && (
                                <AlertTriangle className="w-4 h-4 text-red-500 inline ml-2" />
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">{formatDate(mandate.next_execution)}</td>
                            <td className="px-4 py-3 text-sm">{mandate.total_executions || 0} debits</td>
                            <td className="px-4 py-3">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleViewMandate(mandate)}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Pagination */}
                {mandates.length > 0 && (
                  <div className="px-4 py-3 border-t flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      Page {mandatesPage} • {mandatesTotal} total
                    </span>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={mandatesPage === 1}
                        onClick={() => fetchMandates(mandatesPage - 1)}
                      >
                        Previous
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={!mandatesHasMore}
                        onClick={() => fetchMandates(mandatesPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Open Alerts ({openAlerts})</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchAlerts}>
                  <RefreshCw className={`w-4 h-4 ${alertsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </CardHeader>
              <CardContent>
                {alertsLoading ? (
                  <div className="p-4 text-center text-gray-500">Loading...</div>
                ) : alerts.length === 0 ? (
                  <div className="p-8 text-center">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-500">No open alerts! All SIPs running smoothly.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div 
                        key={alert.alert_id}
                        className="border border-red-200 bg-red-50 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="w-4 h-4 text-red-600" />
                              <span className="font-medium text-red-800">{alert.type}</span>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{alert.reason}</p>
                            <div className="text-xs text-gray-500">
                              <span>User: {alert.user_name} ({alert.user_phone})</span>
                              <span className="mx-2">•</span>
                              <span>Amount: {formatCurrency(alert.amount)}</span>
                              <span className="mx-2">•</span>
                              <span>Retries: {alert.retry_count}</span>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedAlert(alert);
                              setShowResolveAlert(true);
                            }}
                          >
                            Resolve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Scheduler Tab */}
        {activeTab === 'scheduler' && (
          <div>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Scheduler Jobs</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchSchedulerStatus}>
                  <RefreshCw className={`w-4 h-4 ${schedulerLoading ? 'animate-spin' : ''}`} />
                </Button>
              </CardHeader>
              <CardContent>
                {schedulerLoading ? (
                  <div className="p-4 text-center text-gray-500">Loading...</div>
                ) : !schedulerStatus ? (
                  <div className="p-4 text-center text-gray-500">Failed to load scheduler status</div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="font-medium">
                        Scheduler is {schedulerStatus.scheduler_running ? 'Running' : 'Stopped'}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {schedulerStatus.sip_jobs?.map((job) => (
                        <div 
                          key={job.job_id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div>
                            <div className="font-medium">{job.name}</div>
                            <div className="text-xs text-gray-500">
                              Next run: {job.next_run_time ? formatDate(job.next_run_time) : 'N/A'}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTriggerJob(job.job_id)}
                            disabled={triggeringJob === job.job_id}
                          >
                            {triggeringJob === job.job_id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Zap className="w-4 h-4 mr-1" />
                                Run Now
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Mandate Detail Dialog */}
      <Dialog open={showMandateDetail} onOpenChange={setShowMandateDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mandate Details</DialogTitle>
          </DialogHeader>
          {selectedMandate && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">User</p>
                  <p className="font-medium">{selectedMandate.user_name}</p>
                  <p className="text-sm text-gray-600">{selectedMandate.user_phone}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Mandate ID</p>
                  <p className="font-mono text-sm">{selectedMandate.mandate_id}</p>
                </div>
              </div>

              {/* SIP Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Amount</p>
                  <p className="font-bold text-lg">{formatCurrency(selectedMandate.amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Frequency</p>
                  <p className="font-medium capitalize">{selectedMandate.frequency}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(selectedMandate.status)}`}>
                    {selectedMandate.status}
                  </span>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Start Date</p>
                  <p className="text-sm">{formatDate(selectedMandate.start_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">End Date</p>
                  <p className="text-sm">{formatDate(selectedMandate.end_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Next Execution</p>
                  <p className="text-sm font-medium">{formatDate(selectedMandate.next_execution)}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Total Executions</p>
                  <p className="font-bold">{selectedMandate.total_executions || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Invested</p>
                  <p className="font-bold">{formatCurrency(selectedMandate.total_invested || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Millis Earned</p>
                  <p className="font-bold">{(selectedMandate.total_milli_earned || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Review Status */}
              {selectedMandate.needs_review && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-800">Needs IT Review</span>
                  </div>
                  <p className="text-sm text-red-700">{selectedMandate.review_reason}</p>
                  <p className="text-xs text-red-500 mt-1">Retry count: {selectedMandate.retry_count}</p>
                </div>
              )}

              {/* Execution History */}
              {selectedMandate.execution_history && selectedMandate.execution_history.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Recent Executions</p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {selectedMandate.execution_history.slice(-5).reverse().map((exec, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                        <span>{formatDate(exec.date)}</span>
                        <span>{formatCurrency(exec.amount)}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          exec.status === 'success' ? 'bg-green-100 text-green-800' :
                          exec.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {exec.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {selectedMandate?.status === 'active' && (
              <Button 
                variant="default"
                onClick={() => setShowExecuteConfirm(true)}
              >
                <Zap className="w-4 h-4 mr-2" />
                Manual Execute
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowMandateDetail(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Execute Confirmation Dialog */}
      <Dialog open={showExecuteConfirm} onOpenChange={setShowExecuteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Manual Execution</DialogTitle>
            <DialogDescription>
              This will trigger an immediate SIP debit for this mandate. Please provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Reason for manual execution..."
              value={executeReason}
              onChange={(e) => setExecuteReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExecuteConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleExecuteMandate} disabled={executing}>
              {executing ? 'Executing...' : 'Confirm Execute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Alert Dialog */}
      <Dialog open={showResolveAlert} onOpenChange={setShowResolveAlert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Alert</DialogTitle>
            <DialogDescription>
              Provide resolution details for this SIP alert.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Action Taken</label>
              <Select 
                value={resolveForm.action_taken} 
                onValueChange={(v) => setResolveForm({...resolveForm, action_taken: v})}
              >
                <SelectTrigger className="w-full mt-1">
                  <SelectValue placeholder="Select action..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="retry">Retried Execution</SelectItem>
                  <SelectItem value="cancel">Cancelled Mandate</SelectItem>
                  <SelectItem value="contact_user">Contacted User</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Resolution Notes</label>
              <Textarea
                placeholder="Describe what was done to resolve this..."
                value={resolveForm.resolution}
                onChange={(e) => setResolveForm({...resolveForm, resolution: e.target.value})}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveAlert(false)}>
              Cancel
            </Button>
            <Button onClick={handleResolveAlert}>
              Resolve Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSIPManagementPage;
